import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, useProperty, usePropertyAddressUrl, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import {
  repliWhiteLogoUrl,
  MultihubLogoMonoWhite,
  FacebookIcon,
  InstagramIcon,
  YouTubeIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types & Helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/**
 * Resolve a "Text Us" URL from the property's custom_fields array.
 *
 * Scans every custom field looking for one whose ID or label is
 * recognizably the Multihub "Custom Text Us URL" field — regardless of
 * the exact snake_case ID Multihub ends up using (custom_text_us_url,
 * custom_text_us, custom_textus_url, custom_sms_url, etc.).
 *
 * Returns the first non-empty value found, or null if no match exists.
 */
function resolveCustomTextUsUrl(property: unknown): string | null {
  if (!property || typeof property !== 'object') return null;
  const prop = property as Record<string, unknown>;

  // 1. Try flattened top-level keys first (fast path)
  const topLevelCandidates = [
    'custom_text_us_url',
    'custom_textus_url',
    'custom_text_us',
    'custom_textus',
    'custom_sms_url',
    'custom_text_me_url',
  ];
  for (const key of topLevelCandidates) {
    const v = prop[key];
    if (typeof v === 'string' && hasValue(v)) return v;
  }

  // 2. Fallback: scan the raw custom_fields array for any field whose ID
  //    or label looks like a "text us" URL field. This catches variants
  //    like custom_text_us, custom_textus_url, or a human-entered label
  //    such as "Custom Text Us URL".
  const cfs = prop.custom_fields;
  if (Array.isArray(cfs)) {
    const pattern = /text[\s_-]*us|textus|text[\s_-]*me|sms/i;
    for (const cf of cfs) {
      if (!cf || typeof cf !== 'object') continue;
      const c = cf as Record<string, unknown>;
      const idStr = typeof c.ID === 'string' ? c.ID : '';
      const labelStr = typeof c.label === 'string' ? c.label : '';
      const nameStr = typeof c.name === 'string' ? c.name : '';
      const haystack = `${idStr} ${labelStr} ${nameStr}`;
      if (pattern.test(haystack)) {
        const val = c.value;
        if (typeof val === 'string' && hasValue(val)) return val;
      }
    }
  }

  return null;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelHref(raw: string | null | undefined): string {
  const cleaned = (raw ?? '').replace(/[^+\d]/g, '');
  return cleaned ? `tel:${cleaned}` : '#';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHours { label: string; hours: string; }

function normalizeDay(day: string): string { return day.trim().toLowerCase().slice(0, 3); }

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];
  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    groups.push({ label: dayLabel, hours: hoursStr });
  }
  return groups;
}

// ============================================================================
// RentCafe / Conversations chat launcher stacking
// ============================================================================

// Just above the open header / offer modal stack (z-index 100). Vendor often
// sets an inline z-index, so stylesheet + setProperty both use !important.
const CHAT_LAUNCHER_Z_INDEX = '120';
const CHAT_LAUNCHER_SELECTOR =
  '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
const CHAT_WRAP_CLASS = 'rcc-app-cta-wrap';
const CHAT_LAUNCHER_STYLE_ID = 'preferred-chat-launcher-z';

function isChatLauncher(el: Element): el is HTMLElement {
  return (
    el instanceof HTMLElement &&
    (el.classList.contains(CHAT_WRAP_CLASS) ||
      el.classList.contains('rcc-app-controls') ||
      el.id === 'conversations-webchat-toggle')
  );
}

function pinChatLauncherZ(el: HTMLElement) {
  if (
    el.style.getPropertyValue('z-index') === CHAT_LAUNCHER_Z_INDEX &&
    el.style.getPropertyPriority('z-index') === 'important'
  ) {
    return;
  }
  el.style.setProperty('z-index', CHAT_LAUNCHER_Z_INDEX, 'important');

  // Only pin an outermost fixed ancestor when it *is* the chat wrap —
  // never a random fixed page shell the launcher happens to sit under.
  if (el.classList.contains(CHAT_WRAP_CLASS)) return;

  let node: HTMLElement | null = el.parentElement;
  let outermostFixed: HTMLElement | null = null;
  while (node && node !== document.documentElement) {
    if (getComputedStyle(node).position === 'fixed') {
      outermostFixed = node;
    }
    node = node.parentElement;
  }
  if (outermostFixed?.classList.contains(CHAT_WRAP_CLASS)) {
    if (
      outermostFixed.style.getPropertyValue('z-index') !== CHAT_LAUNCHER_Z_INDEX ||
      outermostFixed.style.getPropertyPriority('z-index') !== 'important'
    ) {
      outermostFixed.style.setProperty('z-index', CHAT_LAUNCHER_Z_INDEX, 'important');
    }
  }
}

function scanAndPinChatLaunchers(
  root: ParentNode = document,
  onLauncher?: (el: HTMLElement) => void,
) {
  root.querySelectorAll(CHAT_LAUNCHER_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    pinChatLauncherZ(node);
    onLauncher?.(node);
  });
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_secondary_logo', 'property_primary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.youtube.com/@WeidnerApartmentHomes', suggestedFields: ['property_youtube_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://multihub.ai' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Agency' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://repli.com' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const weidnerCaresLogoDef: SlotGroupDefinition = {
  label: 'Weidner Cares Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/weidnercares' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const loveNotesLogoDef: SlotGroupDefinition = {
  label: 'Love Notes Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/lovenotes' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    url: {
      label: 'Custom Text Us URL',
      type: 'url',
      description:
        'Optional. If set, the Text Us link opens this URL in a new window. ' +
        'If left empty, the component auto-detects the "Custom Text Us URL" ' +
        'multihub custom field on the property (matching common variants ' +
        'like custom_text_us_url, custom_textus_url, custom_sms_url, etc.). ' +
        'If no custom field is found, the Text Us link is hidden.',
      defaultField: 'custom_text_us_url',
      suggestedFields: [
        'custom_text_us_url',
        'custom_textus_url',
        'custom_text_us',
        'custom_sms_url',
        'property_website_url',
      ],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Text Us' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Inline SVG Fallback Logos
// ============================================================================

function WeidnerCaresLogoFallback({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 160 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 30L16 10L22 25L28 10L34 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <text x="42" y="24" fill="currentColor" fontFamily="sans-serif" fontSize="11" fontWeight="600" letterSpacing="0.5">WEIDNER</text>
      <text x="42" y="35" fill="currentColor" fontFamily="sans-serif" fontSize="9" fontWeight="400" letterSpacing="1.5">CARES</text>
    </svg>
  );
}

function LoveNotesLogoFallback({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 140 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 14C16 10 20 8 22 10C24 8 28 10 28 14C28 20 22 26 22 26C22 26 16 20 16 14Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <text x="36" y="22" fill="currentColor" fontFamily="sans-serif" fontSize="11" fontWeight="600" letterSpacing="0.5">LOVE</text>
      <text x="36" y="33" fill="currentColor" fontFamily="sans-serif" fontSize="9" fontWeight="400" letterSpacing="1.5">NOTES</text>
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phoneOverride = useContentToken('footer.phone', { defaultValue: '' });
  const phoneFallback = usePropertyField('property_phone') as string | null;
  const phone = phoneOverride || phoneFallback || '(555) 555-5555';
  const addressLine1 = useContentToken('footer.address_line1');
  const managedByText = useContentToken('footer.managed_by_text');
  const poweredByText = useContentToken('footer.powered_by_text');
  const designByText = useContentToken('footer.design_by_text');
  const cortButtonText = useContentToken('footer.cort_button_text');
  const propertyName = usePropertyField('property_name') as string | null;
  const cortLeadFormId = (usePropertyField('custom_cort_lead_form_id') as string) || '';
  const property = useProperty();
  const currentYear = new Date().getFullYear();

  const openCortModal = React.useCallback(() => {
    const cort = typeof window !== 'undefined'
      ? (window as unknown as { Cort_modal?: { open?: () => void } }).Cort_modal
      : undefined;
    cort?.open?.();
  }, []);

  // Address link resolves via usePropertyAddressUrl(), which internally
  // walks the canonical place URL → business URL → full address → lat/lon
  // fallback chain. Replaces the old direct read of the Google place URL.
  const propertyAddressUrl = usePropertyAddressUrl();
  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const zip = usePropertyField('property_address_zip') as string | null;

  const addressLine2 = [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '');

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtubeLink = useSlotGroup('footer.youtube_link', youtubeLinkDef);
  const weidnerCaresLogo = useSlotGroup('footer.weidner_cares_logo', weidnerCaresLogoDef);
  const loveNotesLogo = useSlotGroup('footer.love_notes_logo', loveNotesLogoDef);
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const resolvedTextUsUrl = hasValue(textUsLink.url)
    ? (textUsLink.url as string)
    : resolveCustomTextUsUrl(property);
  const hasTextUsUrl = hasValue(resolvedTextUsUrl);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  const hasSocial = !!(facebookLink.url || instagramLink.url || youtubeLink.url);

  // Keep the RentCafe / Conversations launcher above chrome but below modals that
  // intentionally cover the page. Stylesheet beats non-!important vendor CSS;
  // the observer re-applies setProperty(..., 'important') when the vendor
  // rewrites inline z-index after mount.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    let styleEl = document.getElementById(CHAT_LAUNCHER_STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = CHAT_LAUNCHER_STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      ${CHAT_LAUNCHER_SELECTOR} {
        z-index: ${CHAT_LAUNCHER_Z_INDEX} !important;
      }
    `;

    const watchedLaunchers = new WeakSet<HTMLElement>();
    const styleObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') continue;
        const target = mutation.target;
        if (target instanceof Element && isChatLauncher(target)) pinChatLauncherZ(target);
      }
    });

    const watchLauncher = (el: HTMLElement) => {
      if (watchedLaunchers.has(el)) return;
      watchedLaunchers.add(el);
      styleObserver.observe(el, { attributes: true, attributeFilter: ['style'] });
    };

    scanAndPinChatLaunchers(document, watchLauncher);

    const treeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (isChatLauncher(node)) {
            pinChatLauncherZ(node);
            watchLauncher(node);
          }
          scanAndPinChatLaunchers(node, watchLauncher);
        });
      }
    });

    treeObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      treeObserver.disconnect();
      styleObserver.disconnect();
      styleEl?.remove();
    };
  }, []);

  return (
    <footer className="footer-main">
      {/* ── Primary band — brand-colored with logo, nav, contact, hours ── */}
      <div className="footer-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-8">

            {/* Col 1: Logo + tagline + social + Weidner program logos */}
            <div className="lg:col-span-1 text-center md:text-left">
              <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
                <a href="/" className="inline-block md:block mb-5">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-12 w-auto max-w-[180px] object-contain"
                    />
                  ) : (
                    <div className="font-heading text-2xl text-white">Our Community</div>
                  )}
                </a>
              </EditableSlotGroup>

              <div className="footer-divider mb-5 mx-auto md:mx-0" />

              {hasSocial && (
                <div className="flex items-center justify-center md:justify-start gap-3">
                  {facebookLink.url && (
                    <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                      <a href={facebookLink.url} target={facebookLink._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className="footer-social-icon">
                        <FacebookIcon size={18} />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {instagramLink.url && (
                    <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                      <a href={instagramLink.url} target={instagramLink._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className="footer-social-icon">
                        <InstagramIcon size={18} />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {youtubeLink.url && (
                    <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
                      <a href={youtubeLink.url} target={youtubeLink._target || '_blank'} rel="noopener noreferrer" aria-label="YouTube" className="footer-social-icon">
                        <YouTubeIcon size={18} />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}

              {/* Weidner Cares & Love Notes logos */}
              <div className={`flex items-center justify-center md:justify-start gap-2 ${hasSocial ? 'mt-4' : ''}`}>
                <EditableSlotGroup groupPath="footer.weidner_cares_logo" definition={weidnerCaresLogoDef}>
                  <a
                    href={weidnerCaresLogo.url || 'https://www.weidner.com/weidnercares'}
                    target={weidnerCaresLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label="Weidner Cares"
                    className="footer-program-logo"
                  >
                    {weidnerCaresLogo.image ? (
                      <VibesitesImage
                        url={weidnerCaresLogo.image}
                        alt={weidnerCaresLogo.alt || 'Weidner Cares'}
                        className="h-10 w-auto max-w-[120px] object-contain"
                      />
                    ) : (
                      <WeidnerCaresLogoFallback className="h-10 w-auto text-white" />
                    )}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="footer.love_notes_logo" definition={loveNotesLogoDef}>
                  <a
                    href={loveNotesLogo.url || 'https://www.weidner.com/lovenotes'}
                    target={loveNotesLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label="Love Notes"
                    className="footer-program-logo"
                  >
                    {loveNotesLogo.image ? (
                      <VibesitesImage
                        url={loveNotesLogo.image}
                        alt={loveNotesLogo.alt || 'Love Notes'}
                        className="h-10 w-auto max-w-[120px] object-contain"
                      />
                    ) : (
                      <LoveNotesLogoFallback className="h-10 w-auto text-white" />
                    )}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Col 2: Contact */}
            <div className="text-center md:text-left">
              <p className="footer-col-label">Contact</p>

              <div className="mb-4 flex items-center justify-center md:justify-start gap-3">
                <a href={toTelHref(phone)} type="call" className="dniphone dniphonehref footer-link inline-block">
                  <EditableToken path="footer.phone" defaultField="property_call_tracking_phone" defaultValue="(555) 555-5555">
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
                {hasTextUsUrl && (
                  <>
                    <span className="text-white/40" aria-hidden="true">|</span>
                    <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                      <a
                        href={resolvedTextUsUrl as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-link inline-flex items-center gap-1.5"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        {textUsLink.label || 'Text Us'}
                      </a>
                    </EditableSlotGroup>
                  </>
                )}
              </div>

              <a
                href={propertyAddressUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link block leading-relaxed"
              >
                <EditableToken path="footer.address_line1" defaultField="property_address_1" defaultValue="123 Main Street" as="span" className="block">
                  {addressLine1}
                </EditableToken>
                {addressLine2 && <span className="block">{addressLine2}</span>}
              </a>
            </div>

            {/* Col 4: Office Hours */}
            <div className="text-center md:text-left">
              <p className="footer-col-label">Office Hours</p>
              {groupedHours.length > 0 ? (
                <dl className="space-y-1.5">
                  {groupedHours.map((group, index) => (
                    <div key={index} className="flex justify-between font-body text-sm text-white">
                      <dt>{group.label}</dt>
                      <dd className="text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-sm text-white/40">Office hours will be displayed here.</p>
              )}

              {/* CORT lead form CTA — hidden unless the property has a CORT form ID */}
              {cortLeadFormId ? (
                <div className="mt-5 flex justify-center md:justify-start">
                  <button
                    type="button"
                    onClick={openCortModal}
                    className="footer-cort-btn group/cort relative inline-flex items-center justify-center gap-2 rounded-full text-[var(--vibe-color-text-on-primary,white)] font-semibold uppercase transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%)',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2), 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent)',
                    }}
                  >
                    <EditableToken path="footer.cort_button_text" as="span" className="footer-cort-btn-label relative z-10">
                      {cortButtonText || 'Furnish Your Home with CORT'}
                    </EditableToken>
                    <svg className="footer-cort-btn-icon relative z-10 shrink-0 transition-transform duration-300 group-hover/cort:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar — darker, compliance + attribution ── */}
      <div className="footer-bottom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Compliance icons + legal */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-5 text-white/70">
              <FairHousingIcon size={28} color="currentColor" />
              <PetsAllowedIcon size={28} color="currentColor" />
              <WheelchairIcon size={28} color="currentColor" />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-white/70" aria-label="Legal links">
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-white transition-colors">
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">·</span>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-white transition-colors">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">·</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-white transition-colors">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>
          </div>

          {/* Attribution row */}
          <div className="footer-attribution-row flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
            {/* Managed By */}
            <div className="flex items-center gap-2">
              <EditableToken path="footer.managed_by_text" defaultValue="Professionally Managed by" as="span" className="font-body text-[10px] text-white/70">
                {managedByText}
              </EditableToken>
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer">
                    <VibesitesImage url={managedByLogo.image} alt={managedByLogo.alt || 'Management Company'} className="h-6 w-auto max-w-[120px] object-contain opacity-80 hover:opacity-100 transition-opacity" />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>

            {/* Copyright */}
            <p className="font-body text-[10px] text-white/70 order-last md:order-none">
              © {currentYear} All Rights Reserved.{propertyName ? ` ${propertyName}` : ''}
            </p>

            {/* Powered By + Design By */}
            <div className="flex items-center gap-5">
              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.powered_by_text" defaultValue="Powered by" as="span" className="font-body text-[10px] text-white/70">
                    {poweredByText}
                  </EditableToken>
                  {poweredByLogo.image ? (
                    <a href={poweredByLogo.url || 'https://multihub.ai'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                      <VibesitesImage url={poweredByLogo.image} alt={poweredByLogo.alt || 'Multihub'} className="h-4 w-auto max-w-[100px] object-contain" />
                    </a>
                  ) : (
                    <a href="https://multihub.ai" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                      <MultihubLogoMonoWhite height={16} alt="Multihub" />
                    </a>
                  )}
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.design_by_text" defaultValue="Website Design by" as="span" className="font-body text-[10px] text-white/70">
                    {designByText}
                  </EditableToken>
                  {designByLogo.image ? (
                    <a href={designByLogo.url || 'https://repli.com'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                      <VibesitesImage url={designByLogo.image} alt={designByLogo.alt || 'Agency'} className="h-4 w-auto max-w-[100px] object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                    </a>
                  ) : (
                    <a href="https://repli.com" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                      <VibesitesImage url={repliWhiteLogoUrl} alt="Repli" className="h-[18px] w-auto" />
                    </a>
                  )}
                </div>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Primary footer band — darkened secondary for legibility */
        .footer-primary {
          background: linear-gradient(
            160deg,
            color-mix(in srgb, var(--vibe-color-secondary) 75%, black) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 60%, black) 100%
          );
        }

        /* Bottom bar — deeper, darker shade */
        .footer-bottom {
          background: color-mix(in srgb, var(--vibe-color-secondary) 70%, black);
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        .footer-attribution-row {
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        /* Column label */
        .footer-col-label {
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: white;
          margin-bottom: 1rem;
        }

        /* Nav/contact links */
        .footer-link {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
          transition: color 0.2s ease;
        }
        .footer-link:hover {
          color: white;
        }

        /* Divider under logo */
        .footer-divider {
          width: 40px;
          height: 2px;
          background: white;
          opacity: 0.6;
        }

        /* Social icon circles */
        .footer-social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.6);
          transition: all 0.25s ease;
        }
        .footer-social-icon:hover {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: white;
        }

        /* Program logos (Weidner Cares, Love Notes) — full brightness to match body text */
        .footer-program-logo {
          display: inline-flex;
          opacity: 1;
          transition: opacity 0.25s ease;
        }
        .footer-program-logo:hover {
          filter: brightness(1.08);
        }

        /* CORT CTA under office hours — scale across phone / tablet / desktop */
        .footer-cort-btn {
          width: 100%;
          max-width: 20rem;
          padding: 0.625rem 1.25rem;
          font-size: 0.75rem;
          letter-spacing: 0.1em;
          line-height: 1.25;
        }
        .footer-cort-btn-label {
          text-align: center;
          text-wrap: balance;
          line-height: 1.3;
        }
        .footer-cort-btn-icon {
          width: 0.875rem;
          height: 0.875rem;
        }

        /* Tablet (3-col footer): use full column width, tighter type so label stays ~1–2 lines */
        @media (min-width: 768px) and (max-width: 1023px) {
          .footer-cort-btn {
            max-width: none;
            width: 100%;
            padding: 0.625rem 0.875rem;
            font-size: 0.6875rem;
            letter-spacing: 0.06em;
            gap: 0.375rem;
          }
          .footer-cort-btn-icon {
            width: 0.75rem;
            height: 0.75rem;
          }
        }

        /* Desktop: single-line pill when the hours column is wide enough */
        @media (min-width: 1024px) {
          .footer-cort-btn {
            width: auto;
            max-width: 100%;
            padding: 0.75rem 1.5rem;
            font-size: 0.8125rem;
            letter-spacing: 0.12em;
            white-space: nowrap;
          }
          .footer-cort-btn-label {
            text-align: left;
            text-wrap: unset;
          }
          .footer-cort-btn-icon {
            width: 1rem;
            height: 1rem;
          }
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.address_line1': {
      label: 'Street Address',
      type: 'text',
      defaultField: 'property_address_1',
      defaultValue: '123 Main Street',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'text',
      defaultValue: 'Professionally Managed by',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'text',
      defaultValue: 'Powered by',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'text',
      defaultValue: 'Website Design by',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.weidner_cares_logo': weidnerCaresLogoDef,
    'footer.love_notes_logo': loveNotesLogoDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
